/**
 * Rewrites a Supabase Storage public object URL to the /render/image endpoint
 * so it comes back resized + re-encoded (WebP negotiated automatically).
 *
 * Non-Supabase URLs, already-transformed URLs, and SVGs are returned as-is.
 * Callers still provide onError fallbacks in case the transform fails.
 */
export const optimizeStorageUrl = (
  url: string,
  width?: number,
  height?: number,
  quality: number = 75,
): string => {
  if (!url) return "";
  if (!url.includes("/storage/v1/object/public/")) return url;
  if (url.includes("/storage/v1/render/image/public/")) return url;
  if (/\.svg(\?|$)/i.test(url)) return url;

  const transformed = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
  const params = new URLSearchParams();
  if (width) params.set("width", String(width));
  if (height) params.set("height", String(height));
  params.set("quality", String(quality));
  params.set("resize", "cover");
  return `${transformed}${transformed.includes("?") ? "&" : "?"}${params.toString()}`;
};

/**
 * Builds a responsive srcset string for a storage image at multiple widths.
 * Returns an empty string for non-Supabase URLs so the caller can skip srcset.
 */
export const buildStorageSrcSet = (
  url: string,
  widths: number[] = [400, 800, 1200],
  quality: number = 75,
): string => {
  if (!url || !url.includes("/storage/v1/object/public/")) return "";
  return widths
    .map((w) => `${optimizeStorageUrl(url, w, undefined, quality)} ${w}w`)
    .join(", ");
};
